import React, { useContext, useEffect, useRef, useState } from 'react';
import { UNSAFE_LocationContext } from 'react-router-dom';
import CommitteeCard from './CommitteeCard';
import { appFromPath, type CommitteeBrief } from './committeeTypes';

export interface CommitteeLinkProps {
  /** TRACER co_id. Omit only when the payload truly lacks it — the card then resolves the name. */
  coId?: string | null;
  name: string;
  className?: string;
  /** A brief already in hand (the /design-system demo, tests); skips the fetch. */
  brief?: CommitteeBrief | null;
  /**
   * Custom trigger content (e.g. the citizen measure card's committee row). The
   * button then takes `className` as its whole style — no inline-link classes.
   */
  children?: React.ReactNode;
  /** Accessible name when `children` isn't just the committee name. */
  ariaLabel?: string;
  /** The measure the card was opened from; the card marks that row "This measure". */
  contextMeasureId?: string;
}

/**
 * A committee name that opens the committee card (PRD §5.11). Every surface that
 * shows a campaign committee's name renders it through this — never a
 * name-prefill link into the finance explorer or a fresh name search
 * (`committeeLinkConventions.test.ts`).
 *
 * A text button styled as a link: it takes its color and font from the
 * surrounding text, underlines on hover/focus. In the citizen app the hit area is
 * stretched to 44px tall with padding offset by negative margin, so the line
 * layout doesn't move.
 */
const CommitteeLink: React.FC<CommitteeLinkProps> = ({ coId, name, className = '', brief, children, ariaLabel, contextMeasureId }) => {
  const [open, setOpen] = useState(false);
  const wasOpen = useRef(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Read the route without requiring one: the link also renders in isolated
  // component tests and widgets mounted outside a <Router>.
  const routed = useContext(UNSAFE_LocationContext)?.location?.pathname;
  const citizen = appFromPath(routed ?? (typeof window !== 'undefined' ? window.location.pathname : '/')) === 'citizen';

  // Focus returns to the name on close. The Modal restores whatever was focused
  // when it opened, but a click doesn't focus a button in every browser (Safari).
  useEffect(() => {
    if (open) {
      wasOpen.current = true;
    } else if (wasOpen.current) {
      wasOpen.current = false;
      triggerRef.current?.focus();
    }
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-label={ariaLabel}
        onClick={(event) => {
          // Committee names often sit inside clickable rows and cards.
          event.stopPropagation();
          setOpen(true);
        }}
        className={children ? className : `cursor-pointer text-left underline-offset-2 hover:underline focus:outline-none focus-visible:underline focus-visible:ring-2 focus-visible:ring-primary-500 rounded-sm ${
          citizen ? 'inline-block -my-[10px] py-[10px]' : ''
        } ${className}`}
      >
        {children ?? name}
      </button>
      {open && (
        <CommitteeCard isOpen={open} onClose={() => setOpen(false)} coId={coId} name={name} brief={brief} contextMeasureId={contextMeasureId} />
      )}
    </>
  );
};

export default CommitteeLink;
